<script setup>
import Button from "primevue/button";
import {reactive, ref, toRaw, watch} from "vue";
import ServiceForm from "./ServiceForm.vue";
import { trans } from 'laravel-vue-i18n';

const valid = defineModel('valid');
const model = defineModel()

// -1 = přidání nové, i >= 0 = editace i-té služby, null = nic
const expandedIndex = ref(null)
const editingService = reactive(emptyService())

const errors = reactive({
    name: '',
    price: '',
    min_price: '',
    max_price: '',
    length_in_minutes: '',
    description: '',
})

function emptyService() {
    return {
        name: '',
        price_preference: 'fixed',
        price: null,
        min_price: null,
        max_price: null,
        currency: 'CZK',
        description: '',
        image: null,
        length_in_minutes: 5,
    }
}

function startAdding() {
    Object.assign(editingService, emptyService())
    resetErrors()
    expandedIndex.value = -1
}

function startEditing(i) {
    Object.assign(editingService, structuredClone(toRaw(model.value.services[i])))
    resetErrors()
    expandedIndex.value = i
}

function cancel() {
    expandedIndex.value = null
}

function resetErrors() {
    errors.name = ''
    errors.price = ''
    errors.min_price = ''
    errors.max_price = ''
    errors.length_in_minutes = ''
    errors.description = ''
}

function validate() {
    resetErrors()
    let ok = true

    if (!editingService.name?.trim()) {
        errors.name = trans('forms.service_error_name')
        ok = false
    }

    if (editingService.price_preference === 'fixed') {
        if (editingService.price === null || editingService.price < 0) {
            errors.price = trans('forms.service_error_price')
            ok = false
        }
    } else {
        if (editingService.min_price === null || editingService.min_price < 0) {
            errors.min_price = trans('forms.service_error_min_price')
            ok = false
        }
        if (editingService.max_price === null || editingService.max_price < editingService.min_price) {
            errors.max_price = trans('forms.service_error_max_price')
            ok = false
        }
    }

    if (!editingService.length_in_minutes || editingService.length_in_minutes < 5) {
        errors.length_in_minutes = trans('forms.service_error_length')
        ok = false
    }

    if (!editingService.description?.trim()) {
        errors.description = trans('forms.service_error_description')
        ok = false
    }

    return ok
}

function save() {
    if (!validate()) return

    if (expandedIndex.value === -1) {
        model.value.services.push(structuredClone(toRaw(editingService)))
    } else {
        model.value.services[expandedIndex.value] = structuredClone(toRaw(editingService))
    }

    expandedIndex.value = null
}

function remove(i) {
    model.value.services.splice(i, 1)
    if (expandedIndex.value === i) {
        expandedIndex.value = null
    }
}

function formatPrice(service) {
    if (service.min_price !== null && service.max_price !== null) {
        return `${service.min_price} – ${service.max_price} Kč`
    }
    if (service.price !== null) {
        return `${service.price} Kč`
    }
    return '—'
}

watch(() => model.value.services, (now) => {
    valid.value = now.length !== 0
}, {immediate: true, deep: true})
</script>

<template>
    <div class="flex flex-col gap-3">
        <template v-for="(_service, i) in model.services" :key="i">
            <!-- Inline formulář pro editaci -->
            <div v-if="expandedIndex === i" class="rounded-xl border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/30 p-5">
                <h3 class="font-semibold text-gray-800 dark:text-gray-100 mb-4">{{ $t('forms.service_edit_title') }}</h3>
                <ServiceForm v-model="editingService" :errors="errors"/>
                <div class="flex justify-end gap-3 mt-5 pt-4 border-t border-blue-200 dark:border-blue-800">
                    <Button :label="$t('common.cancel')" severity="secondary" outlined @click="cancel"/>
                    <Button :label="$t('common.save')" @click="save"/>
                </div>
            </div>

            <!-- Kompaktní karta služby -->
            <div
                v-else
                class="flex items-center gap-4 rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-4 py-3"
            >
                <div v-if="_service.image" class="w-12 h-12 rounded-lg overflow-hidden shrink-0">
                    <img :src="_service.image" alt="" class="w-full h-full object-cover"/>
                </div>
                <div v-else class="w-12 h-12 rounded-lg bg-gray-100 dark:bg-neutral-700 flex items-center justify-center shrink-0">
                    <i class="pi pi-image opacity-40 text-lg"/>
                </div>

                <div class="grow min-w-0">
                    <p class="font-semibold text-gray-900 dark:text-white truncate">{{ _service.name }}</p>
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                        {{ _service.length_in_minutes }} min · {{ formatPrice(_service) }}
                    </p>
                </div>

                <div class="flex items-center gap-1 shrink-0">
                    <Button
                        icon="pi pi-pencil"
                        rounded text severity="secondary"
                        v-tooltip.bottom="$t('common.edit')"
                        @click="startEditing(i)"
                    />
                    <Button
                        icon="pi pi-trash"
                        rounded text severity="danger"
                        v-tooltip.bottom="$t('common.delete')"
                        @click="remove(i)"
                    />
                </div>
            </div>
        </template>

        <!-- Inline formulář pro přidání nové služby -->
        <div v-if="expandedIndex === -1" class="rounded-xl border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/30 p-5">
            <h3 class="font-semibold text-gray-800 dark:text-gray-100 mb-4">{{ $t('forms.service_new_title') }}</h3>
            <ServiceForm v-model="editingService" :errors="errors"/>
            <div class="flex justify-end gap-3 mt-5 pt-4 border-t border-blue-200 dark:border-blue-800">
                <Button :label="$t('common.cancel')" severity="secondary" outlined @click="cancel"/>
                <Button :label="$t('common.save')" @click="save"/>
            </div>
        </div>

        <!-- Tlačítko přidat, pokud formulář není otevřen -->
        <button
            v-if="expandedIndex === null"
            @click="startAdding"
            class="flex items-center gap-3 rounded-xl border-2 border-dashed border-gray-300 dark:border-neutral-600 px-4 py-3 text-gray-500 dark:text-gray-400 hover:border-gray-400 dark:hover:border-neutral-500 hover:text-gray-700 dark:hover:text-gray-200 transition-colors cursor-pointer"
        >
            <i class="pi pi-plus"/>
            <span class="font-semibold text-sm">{{ $t('forms.service_add_btn') }}</span>
        </button>
    </div>
</template>
